// Client-side aggregation for FCC analytics points. Backend returns daily
// rows; charts bucket them to match legacy PBI layout (monthly for volume,
// refund, chargebacks; weekly for auth rate).

export type BucketPeriod = 'day' | 'week' | 'month'

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n))

const parseDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1))
}

// ISO 8601 week number (Mon-Sun). Returns [isoYear, isoWeek].
const isoWeek = (date: Date): [number, number] => {
  const t = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  )
  const day = t.getUTCDay() || 7
  t.setUTCDate(t.getUTCDate() + 4 - day)
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1))
  const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7)
  return [t.getUTCFullYear(), week]
}

export const bucketKey = (iso: string, period: BucketPeriod): string => {
  if (period === 'day') return iso
  const d = parseDate(iso)
  if (period === 'month') {
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}`
  }
  const [y, w] = isoWeek(d)
  return `${y}-W${pad2(w)}`
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export const bucketLabel = (key: string, period: BucketPeriod): string => {
  if (period === 'month') {
    const [y, m] = key.split('-').map(Number)
    return `${MONTH_NAMES[(m ?? 1) - 1]} ${y}`
  }
  if (period === 'week') {
    const [y, w] = key.split('-W')
    return `Week ${Number(w)} ${y}`
  }
  return key
}

// Aggregate `points` by period, reducing collisions with `reduce`. Output is
// sorted by bucket key. `initial` returns a zeroed accumulator per bucket.
export const bucketPoints = <TPoint extends { date: string }, TAgg>(
  points: TPoint[],
  period: BucketPeriod,
  initial: () => TAgg,
  reduce: (acc: TAgg, point: TPoint) => TAgg,
): Array<{ key: string; agg: TAgg }> => {
  const map = new Map<string, TAgg>()
  for (const point of points) {
    const key = bucketKey(point.date, period)
    map.set(key, reduce(map.get(key) ?? initial(), point))
  }
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, agg]) => ({ key, agg }))
}
